import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { AgeUnit, ChartId, VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AreaLabelConfiguration, AxisConfiguration } from "@/Patient/Models/GrowthCharts/ChartData";
import { CurvesName, GrowthChartsInterpolation } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { BmiCustomAreaHandler } from "../../Charts/BmiCustomAreaHandler";
import { AxisLine } from "../../Charts/GrowthChartGraph";
import { IGrowthChartsCustomAreaHandler } from "../../Charts/IGrowthChartsCustomAreaHandler";
import { GrowthChartModel } from "./GrowthChartModel";

abstract class Age2To19GrowthChart extends GrowthChartModel {
    public get isPlotSelectionSupported(): boolean {
        return false;
    }

    public isDisplayedByDefault(): boolean {
        const age = LuxonDateHelper.dateDiff(this.patient.DateOfBirth, DateTime.nowDate(), "years");
        return age >= 2 && age <= 19;
    }

    public isCompatible(): boolean {
        return super.isCompatible() && this.vitalSigns
            .filter(vital => !!vital[this.vitalProperty])
            .some(vital => this.filterVitalSign(vital));
    }

    public filterVitalSign(vitalSign: MedicalItemVitalSign): boolean {
        if (!this.patient.DateOfBirth)
            return false;

        const months = LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, "months");
        return months >= 24 && months <= 228;
    }

    public get isVitalSign(): boolean {
        return false;
    }

    public get ageUnit(): AgeUnit {
        return "year";
    }

    public get xAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("AgeLabel")} (${cacheStore.GetString("YearAbbrevLabel")})`,
            min: 2,
            max: 19,
            interval: 1
        };
    }
}

class Age2To19WeightForAge extends Age2To19GrowthChart {
    public get id(): ChartId {
        return "WFA219";
    }
    public get name(): string {
        return cacheStore.GetString("Age2To19WeightForAgeLabel");
    }
    public get shortName(): string {
        return cacheStore.GetString("Age2To19WeightForAgeShortLabel");
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "weightMale2-19" : "weightFemale2-19";
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("KilogramUnitSymbolLabel")})`,
                min: 5,
                max: 100,
                interval: 5
            }
            : {
                label: `${cacheStore.GetString("WeightLabel")} (${cacheStore.GetString("PoundUnitSymbolLabel")})`,
                min: 10,
                max: 220,
                interval: 10
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Weight";
    }
}

class Age2To19HeightForAge extends Age2To19GrowthChart {
    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "heightMale2-19" : "heightFemale2-19";
    }
    public get id(): ChartId {
        return "HFA219";
    }
    public get name(): string {
        return cacheStore.GetString("Age2To19HeightForAgeLabel");
    }
    public get shortName(): string {
        return cacheStore.GetString("Age2To19HeightForAgeShortLabel");
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric
            ? {
                label: cacheStore.GetString("HeightLabel") + ` (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 80,
                max: 200,
                interval: 5
            }
            : {
                label: cacheStore.GetString("HeightLabel") + ` (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 30,
                max: 78,
                interval: 2
            };
    }

    public get vitalProperty(): VitalProperty {
        return "Height";
    }
}

class Age2To19BodyMassIndex extends Age2To19GrowthChart {
    constructor(
        patient: Individual,
        vitals: Immutable.List<MedicalItemVitalSign>,
        chartInterpolations: { [key: string]: GrowthChartsInterpolation[] },
        zScores: { [key: number]: { [key: number]: number } },
        unit?: UnitSystem
    ) {
        super(patient, vitals, chartInterpolations, zScores, unit);
    }

    public get hasCustomRegions(): boolean {
        return true;
    }

    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "bmiMale2-19" : "bmiFemale2-19";
    }

    public get id(): ChartId {
        return "BMI219";
    }

    public get growthChartsCustomAreaHandler(): IGrowthChartsCustomAreaHandler {
        return new BmiCustomAreaHandler();
    }

    public getLines(): AxisLine[] {
        const lines = super.getLines();
        lines.push({
            label: null,
            value: 5,
            className: "c3-chart-obesity-value"
        });

        return lines;
    }

    public get name(): string {
        return cacheStore.GetString("Age2To19BodyMassIndexLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("Age2To19BodyMassIndexShortLabel");
    }

    public get yAxis(): AxisConfiguration {
        return {
            label: cacheStore.GetString("MassBodyIndexDLabel"),
            min: 11,
            max: 32,
            interval: 1
        };
    }

    public get vitalProperty(): VitalProperty {
        return "MassBodyIndex";
    }

    public getRegionLabels(): AreaLabelConfiguration[] {
        return [{
            label: cacheStore.GetString("Age2To19BMIObesity"),
            class: "obesity",
            color: "#000000",
            x: 15,
            y: 28.5
        },
        {
            label: cacheStore.GetString("Age2To19BMIOverweight"),
            class: "overweight",
            color: "#737373",
            x: 15,
            y: 25.3
        },
        {
            label: cacheStore.GetString("Age2To19BMIWasting"),
            class: "wasting",
            color: "#737373",
            x: 15,
            y: 15
        },
        {
            label: cacheStore.GetString("Age2To19BMISevereWasting"),
            class: "severe-wasting",
            color: "#000000",
            x: 15,
            y: 13
        }];
    }
}

export { Age2To19BodyMassIndex, Age2To19HeightForAge, Age2To19WeightForAge };
